import { useEffect, useState } from 'react';

export interface LogLine {
  seq: number;
  stream: 'o' | 'e' | 'h';
  line: string;
  at: string;
}

export interface Done {
  status: 'succeeded' | 'failed' | 'cancelled';
  error?: string;
}

/**
 * Il log di un job in tempo reale.
 *
 * EventSource rimanda da solo `Last-Event-ID` alla riconnessione, e l'hub
 * riprende da quel `seq` senza duplicati né buchi: non serve tenere il conto
 * qui. Serve invece chiudere alla ricezione di `end`, altrimenti EventSource
 * si riconnette all'infinito a uno stream che non manderà più niente.
 */
export function useJobLog(slug: string, jobId: string | undefined) {
  const [lines, setLines] = useState<LogLine[]>([]);
  const [done, setDone] = useState<Done | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!jobId) return;
    setLines([]);
    setDone(null);
    setError(false);

    const es = new EventSource(`/api/v1/orgs/${slug}/jobs/${jobId}/logs`);

    es.addEventListener('log', (e) => {
      const line = JSON.parse((e as MessageEvent<string>).data) as LogLine;
      // L'hub garantisce l'assenza di duplicati, ma un `end` seguito da una
      // riconnessione manuale no: lo `seq` è l'unica chiave affidabile.
      setLines((prev) =>
        prev.length > 0 && prev[prev.length - 1]!.seq >= line.seq ? prev : [...prev, line],
      );
    });

    es.addEventListener('end', (e) => {
      setDone(JSON.parse((e as MessageEvent<string>).data) as Done);
      es.close();
    });

    // onerror scatta anche fra due tentativi di riconnessione automatica:
    // è un errore solo se EventSource ha rinunciato.
    es.onerror = () => {
      if (es.readyState === EventSource.CLOSED) setError(true);
    };

    return () => es.close();
  }, [slug, jobId]);

  return { lines, done, error };
}

/** Quante righe si tengono di un log che scorre: oltre, le più vecchie se ne vanno. */
const MAX_RIGHE_TAIL = 2000;

/**
 * Un log seguito in tempo reale — quello di Odoo o di nginx di un'istanza —
 * finché l'URL c'è. Null spegne tutto: è così che il log si apre solo quando
 * qualcuno lo guarda, perché dall'altra parte c'è un `tail -F` via SSH che
 * vive quanto questa connessione.
 *
 * Non c'è `seq` dall'hub, né recupero alla riconnessione: un tail riparte
 * dalle ultime righe. Il progressivo lo si dà qui, per la lista.
 */
export function useTail(url: string | null) {
  const [lines, setLines] = useState<LogLine[]>([]);
  const [ended, setEnded] = useState<Done | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!url) return;
    setLines([]);
    setEnded(null);
    setError(false);

    const es = new EventSource(url);
    es.addEventListener('log', (e) => {
      const line = JSON.parse((e as MessageEvent<string>).data) as LogLine;
      setLines((prev) => {
        const seq = prev.length > 0 ? prev[prev.length - 1]!.seq + 1 : 1;
        const next = prev.length >= MAX_RIGHE_TAIL ? prev.slice(-MAX_RIGHE_TAIL / 2) : prev;
        return [...next, { ...line, seq }];
      });
    });
    es.addEventListener('end', (e) => {
      setEnded(JSON.parse((e as MessageEvent<string>).data) as Done);
      es.close();
    });
    es.onerror = () => {
      if (es.readyState === EventSource.CLOSED) setError(true);
    };
    return () => es.close();
  }, [url]);

  return { lines, ended, error };
}
